import type { UploadTask, UploadTaskSnapshot } from 'firebase/storage';

export const DEFAULT_UPLOAD_INACTIVITY_TIMEOUT_MS = 30_000;

type ResumableUploadOptions = {
    onProgress?: (progress: number) => void;
    inactivityTimeoutMs?: number;
};

export function waitForResumableUpload(
    task: UploadTask,
    options: ResumableUploadOptions = {},
): Promise<void> {
    const inactivityTimeoutMs = options.inactivityTimeoutMs ?? DEFAULT_UPLOAD_INACTIVITY_TIMEOUT_MS;

    return new Promise<void>((resolve, reject) => {
        let settled = false;
        let highestBytesTransferred = 0;
        let timeoutId: ReturnType<typeof setTimeout> | null = null;

        const clearInactivityTimeout = () => {
            if (timeoutId) clearTimeout(timeoutId);
            timeoutId = null;
        };

        const rejectOnce = (error: unknown) => {
            if (settled) return;
            settled = true;
            clearInactivityTimeout();
            reject(error);
        };

        const resolveOnce = () => {
            if (settled) return;
            settled = true;
            clearInactivityTimeout();
            resolve();
        };

        const armInactivityTimeout = () => {
            clearInactivityTimeout();
            timeoutId = setTimeout(() => {
                if (settled) return;
                settled = true;
                task.cancel();
                reject(new Error('The upload stopped responding. Check your connection and try again.'));
            }, inactivityTimeoutMs);
        };

        armInactivityTimeout();
        task.on(
            'state_changed',
            (snapshot: UploadTaskSnapshot) => {
                const progress = snapshot.totalBytes > 0
                    ? Math.round((snapshot.bytesTransferred / snapshot.totalBytes) * 100)
                    : 0;
                options.onProgress?.(progress);
                if (snapshot.bytesTransferred > highestBytesTransferred) {
                    highestBytesTransferred = snapshot.bytesTransferred;
                    armInactivityTimeout();
                }
            },
            rejectOnce,
            resolveOnce,
        );
    });
}
